import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import api, { getImageUrl } from '../config/api';
import moment from 'moment';
import { AuthContext } from '../context/AuthContext';
import PageNavbar from './PageNavbar';
import AppIcon from './icons/AppIcon';
import { getItemDisplayName, shareKitchenCopyWhatsApp } from '../utils/kitchenShareWhatsApp';
import { sendOrderDetailsWhatsApp } from '../utils/sendOrderWhatsApp';
import { shareEventQuotationWhatsApp } from '../utils/shareEventQuotationWhatsApp';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/formatDate';
import './OrderDetails.css';

const PAYMENT_MODES = ['Online', 'Cash', 'Card', 'Cheque', 'Store-1-QR', 'Store-2-QR'];
const SETTLEMENT_TYPES = ['Full', 'Write-off', 'Credit'];

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Add payment
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [addPaymentForm, setAddPaymentForm] = useState({ amount: '', payment_mode: 'Cash', payment_date: moment().format('YYYY-MM-DD'), notes: '' });
  const [addPaymentSubmitting, setAddPaymentSubmitting] = useState(false);

  // Settle modal
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleForm, setSettleForm] = useState({ settlement_type: 'Full', settlement_notes: '' });
  const [settleSubmitting, setSettleSubmitting] = useState(false);

  // Delivery charge (Store Manager, Online Orders only)
  const [updatingDeliveryCharge, setUpdatingDeliveryCharge] = useState(false);

  // Delivery charge modal (Store Manager: must select before printing delivery challan)
  const [showDeliveryChargeModal, setShowDeliveryChargeModal] = useState(false);
  const [deliveryChargeForPrint, setDeliveryChargeForPrint] = useState('');
  const [printingDeliveryChallan, setPrintingDeliveryChallan] = useState(false);

  const [showPostCreateKitchenBanner, setShowPostCreateKitchenBanner] = useState(
    () => Boolean(location.state?.fromCreate)
  );
  const [sendingCustomerWhatsApp, setSendingCustomerWhatsApp] = useState(false);
  const [sharingEventQuote, setSharingEventQuote] = useState(false);

  useEffect(() => {
    if (location.state?.fromCreate) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear router state once on land from Create Order
  }, []);

  const fetchOrder = useCallback(async (bypassCache = false) => {
    try {
      const config = bypassCache ? { params: { _: Date.now() } } : {};
      const response = await api.get(`/orders/${orderId}`, config);
      setOrder(response.data.order);
      
      // Check if can edit (Admin always; creators of the order within 60 days)
      if (user?.role === 'Admin') {
        setCanEdit(true);
      } else if (['Order Creator', 'Cashier', 'Franchise Admin', 'Store Manager', 'Accountant'].includes(user?.role)) {
        const orderDate = moment(response.data.order.created_at);
        const daysDiff = moment().diff(orderDate, 'days');
        const isOwner = Number(response.data.order.created_by) === Number(user.id);
        setCanEdit(daysDiff <= 60 && isOwner);
      } else {
        setCanEdit(false);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  }, [orderId, user]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const doPrintDeliveryChallan = async () => {
    try {
      const response = await api.get(`/pdf/delivery-challan/${orderId}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (printWindow) {
        printWindow.focus();
        fetchOrder();
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        alert('Please allow pop-ups for this site to view the delivery challan.');
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Print error:', error);
      alert(error.response?.data?.error || 'Error generating delivery challan');
    } finally {
      setPrintingDeliveryChallan(false);
    }
  };

  const handlePrintDeliveryChallan = () => {
    const needsDeliveryCharge = ['Store Manager', 'Franchise Admin'].includes(user?.role)
      && order?.category === 'Online Orders'
      && ![100, 150].includes(Number(order?.delivery_charge));
    if (needsDeliveryCharge) {
      setShowDeliveryChargeModal(true);
      setDeliveryChargeForPrint('');
    } else {
      doPrintDeliveryChallan();
    }
  };

  const handleConfirmDeliveryChargeAndPrint = async () => {
    const charge = parseInt(deliveryChargeForPrint, 10);
    if (charge !== 100 && charge !== 150) {
      alert('Please select delivery charge (₹100 or ₹150)');
      return;
    }
    setPrintingDeliveryChallan(true);
    try {
      await api.patch(`/orders/${orderId}/delivery-charge`, { delivery_charge: charge });
      setShowDeliveryChargeModal(false);
      setDeliveryChargeForPrint('');
      await fetchOrder();
      await doPrintDeliveryChallan();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to set delivery charge');
    } finally {
      setPrintingDeliveryChallan(false);
    }
  };

  const handlePrintKitchenCopy = async () => {
    try {
      const response = await api.get(`/pdf/kitchen-copy/${orderId}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (printWindow) {
        printWindow.focus();
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        alert('Please allow pop-ups for this site to view the kitchen copy.');
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Print error:', error);
      alert(error.response?.data?.error || 'Error generating kitchen copy');
    }
  };

  const handleShareKitchenCopyWhatsApp = () => {
    if (!order) return;
    shareKitchenCopyWhatsApp(order, orderId);
  };

  const handlePrintEventQuotation = async () => {
    try {
      const response = await api.get(`/pdf/event-quotation/${orderId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      alert(error.response?.data?.error || 'Error generating event quotation');
    }
  };

  const handleShareEventQuotationWhatsApp = async () => {
    if (!order) return;
    setSharingEventQuote(true);
    try {
      await shareEventQuotationWhatsApp(
        order,
        orderId,
        order.caterer_phone || order.customer_phone
      );
    } finally {
      setSharingEventQuote(false);
    }
  };

  const handleSendOrderToCustomerWhatsApp = async () => {
    if (!order) return;
    if (!order.customer_phone) {
      alert('This order has no customer phone number.');
      return;
    }
    if (!window.confirm(`Send order details on WhatsApp to ${order.customer_phone}?`)) {
      return;
    }
    setSendingCustomerWhatsApp(true);
    try {
      const data = await sendOrderDetailsWhatsApp(orderId);
      alert(data?.message || `Order details sent to ${data?.mobile || order.customer_phone}`);
    } catch (error) {
      console.error('Send customer WhatsApp:', error);
      alert(error.response?.data?.error || error.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingCustomerWhatsApp(false);
    }
  };



  if (loading) {
    return <div className="loading">Loading order details...</div>;
  }

  if (!order) {
    return <div className="container">Order not found</div>;
  }

  const isStoreFrontOps = ['Store Manager', 'Franchise Admin'].includes(user?.role);
  const canPrintDeliveryChallan = ['Store Manager', 'Franchise Admin', 'Admin'].includes(user?.role);
  const canPrintKitchenCopy = true;
  const canChangeStatus = ['Store Manager', 'Franchise Admin', 'Admin'].includes(user?.role);
  const canManagePayments = ['Accountant', 'Admin'].includes(user?.role);
  const showAmounts = user?.role !== 'Store Manager';
  const isOrderCreator = user?.role === 'Order Creator';
  const isSettled = (order.settlement_status || 'Pending') === 'Settled';
  const ORDER_STATUSES = ['Pending', 'In Progress', 'Ready', 'Delivered', 'Cancelled'];
  const isEventCat = ['Events', 'N Caterers', 'Caterers'].includes(order.category);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setAddPaymentSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/payments`, {
        amount: parseFloat(addPaymentForm.amount),
        payment_mode: addPaymentForm.payment_mode,
        payment_date: addPaymentForm.payment_date,
        notes: addPaymentForm.notes || undefined
      });
      setShowAddPayment(false);
      setAddPaymentForm({ amount: '', payment_mode: 'Cash', payment_date: moment().format('YYYY-MM-DD'), notes: '' });
      fetchOrder();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add payment');
    } finally {
      setAddPaymentSubmitting(false);
    }
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    const stype = settleForm.settlement_type;
    if ((stype === 'Write-off' || stype === 'Credit') && !settleForm.settlement_notes.trim()) {
      alert('Notes are required for Write-off or Credit');
      return;
    }
    setSettleSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/settle`, {
        settlement_type: stype,
        settlement_notes: settleForm.settlement_notes.trim() || undefined
      });
      setShowSettleModal(false);
      setSettleForm({ settlement_type: 'Full', settlement_notes: '' });
      fetchOrder();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to settle order');
    } finally {
      setSettleSubmitting(false);
    }
  };

  const handleDeliveryChargeChange = async (value) => {
    if (order.category !== 'Online Orders' || !isStoreFrontOps) return;
    const charge = parseInt(value, 10);
    if (charge !== 100 && charge !== 150) return;
    setUpdatingDeliveryCharge(true);
    try {
      const res = await api.patch(`/orders/${orderId}/delivery-charge`, { delivery_charge: charge });
      // Update local state immediately so dropdown doesn't revert when GET returns 304 (cached) response
      if (res.data && order) {
        setOrder((prev) => prev ? {
          ...prev,
          delivery_charge: res.data.delivery_charge
        } : prev);
      }
      // Refetch with cache-busting so we get fresh data without 304
      fetchOrder(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update delivery charge');
    } finally {
      setUpdatingDeliveryCharge(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === order.status) return;
    setUpdatingStatus(true);
    try {
      await api.patch(`/orders/${orderId}/status`, { status: newStatus });
      setOrder((prev) => ({ ...prev, status: newStatus }));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className={`order-details${isOrderCreator ? ' order-details--order-creator' : ''}`}>
      <PageNavbar />
      <div className="container">
        <div className="order-header">
          <div className="order-header-main">
            <button type="button" className="order-icon-btn" onClick={() => navigate('/orders')} title="Back to orders">
              <AppIcon name="back" size={18} />
              <span>Back</span>
            </button>
            <div className="order-header-titles">
              <h1 className="order-header-title">{order.order_number}</h1>
              {(order.approval_status || 'not_required') === 'awaiting' ? (
                <p className="order-header-sub" style={{ color: '#c2410c', fontWeight: 700 }}>
                  Awaiting admin approval (Online Order under ₹500)
                </p>
              ) : null}
              <p className="order-header-sub">{order.customer_name} · {formatDateDisplay(order.delivery_date)}{order.delivery_time ? ` · ${order.delivery_time}` : ''}</p>
            </div>
          </div>
          <div className="order-actions order-actions--icons" role="toolbar" aria-label="Order actions">
            {isEventCat && (
              <>
                <button type="button" className="order-icon-btn order-icon-btn--primary" onClick={handlePrintEventQuotation} title="View order details PDF">
                  <AppIcon name="print" size={18} />
                  <span>Order PDF</span>
                </button>
                <button
                  type="button"
                  className="order-icon-btn order-icon-btn--whatsapp"
                  onClick={handleShareEventQuotationWhatsApp}
                  disabled={sharingEventQuote}
                  title="Share order details on WhatsApp"
                >
                  <AppIcon name="share" size={18} />
                  <span>{sharingEventQuote ? '…' : 'Share PDF'}</span>
                </button>
              </>
            )}
            {canPrintKitchenCopy && (
              <>
                <button type="button" className="order-icon-btn order-icon-btn--primary" onClick={handlePrintKitchenCopy} title="Print kitchen copy">
                  <AppIcon name="print" size={18} />
                  <span>Kitchen</span>
                </button>
                <button type="button" className="order-icon-btn order-icon-btn--whatsapp" onClick={handleShareKitchenCopyWhatsApp} title="Share kitchen copy on WhatsApp">
                  <AppIcon name="share" size={18} />
                  <span>Share</span>
                </button>
                <button
                  type="button"
                  className="order-icon-btn order-icon-btn--customer"
                  onClick={handleSendOrderToCustomerWhatsApp}
                  disabled={sendingCustomerWhatsApp || !order.customer_phone}
                  title={!order.customer_phone ? 'No customer phone on this order' : 'Send order details to customer WhatsApp'}
                >
                  <AppIcon name="chat" size={18} />
                  <span>{sendingCustomerWhatsApp ? '…' : 'Customer'}</span>
                </button>
              </>
            )}
            {canPrintDeliveryChallan && (
              <button
                type="button"
                className="order-icon-btn order-icon-btn--gold"
                onClick={handlePrintDeliveryChallan}
                disabled={order.delivery_challan_printed && isStoreFrontOps}
                title={order.delivery_challan_printed && isStoreFrontOps ? 'Already printed' : 'Print delivery challan'}
              >
                <AppIcon name="delivery" size={18} />
                <span>{order.delivery_challan_printed && isStoreFrontOps ? 'Printed' : 'Challan'}</span>
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="order-icon-btn order-icon-btn--primary"
                onClick={() => {
                  navigate(`/orders/create?edit=${order.id}`, { state: { editOrder: order } });
                }}
                title="Edit order"
              >
                <AppIcon name="edit" size={18} />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {showPostCreateKitchenBanner && canPrintKitchenCopy && (
          <div className="post-create-kitchen-banner" role="status">
            <div className="post-create-kitchen-banner-text">
              <strong>Order created.</strong> Send order details to the customer on WhatsApp, share the kitchen PDF, or print it. These options are available on <strong>every</strong> order page and on the <strong>Orders</strong> list.
            </div>
            <div className="post-create-kitchen-banner-actions">
              <button type="button" className="btn btn-whatsapp-customer btn-sm" onClick={handleSendOrderToCustomerWhatsApp} disabled={sendingCustomerWhatsApp || !order.customer_phone}>
                {sendingCustomerWhatsApp ? 'Sending…' : 'Send to customer WhatsApp'}
              </button>
              <button type="button" className="btn btn-whatsapp btn-sm" onClick={handleShareKitchenCopyWhatsApp}>
                Share kitchen PDF
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handlePrintKitchenCopy}>
                Print kitchen copy
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowPostCreateKitchenBanner(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isOrderCreator && (canPrintKitchenCopy || isEventCat) && (
          <div className="order-creator-mobile-bottom-actions" aria-label="Quick actions">
            {isEventCat && (
              <>
                <button type="button" className="btn btn-primary" onClick={handlePrintEventQuotation}>
                  View order details PDF
                </button>
                <button
                  type="button"
                  className="btn btn-whatsapp"
                  onClick={handleShareEventQuotationWhatsApp}
                  disabled={sharingEventQuote}
                >
                  {sharingEventQuote ? 'Preparing…' : 'Share order details on WhatsApp'}
                </button>
              </>
            )}
            {canPrintKitchenCopy && (
              <>
                <button
                  type="button"
                  className="btn btn-whatsapp-customer"
                  onClick={handleSendOrderToCustomerWhatsApp}
                  disabled={sendingCustomerWhatsApp || !order.customer_phone}
                >
                  {sendingCustomerWhatsApp ? 'Sending…' : 'Send to customer WhatsApp'}
                </button>
                <button type="button" className="btn btn-whatsapp" onClick={handleShareKitchenCopyWhatsApp}>
                  Share kitchen copy on WhatsApp
                </button>
              </>
            )}
            <Link to="/orders" className="btn btn-secondary">
              ← Back to Orders
            </Link>
          </div>
        )}

        <div className="order-info-grid">
          <div className="card">
            <h2>Order Information</h2>
            <div className="info-row">
              <span className="label">Order Number:</span>
              <span className="value">{order.order_number}</span>
            </div>
            <div className="info-row">
              <span className="label">Category:</span>
              <span className="value">{order.category}</span>
            </div>
            <div className="info-row">
              <span className="label">Status:</span>
              {canChangeStatus ? (
                <select
                  className={`form-control status-select status-${(order.status || '').toLowerCase().replace(/ /g, '-')}`}
                  value={ORDER_STATUSES.includes(order.status) ? order.status : 'Pending'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  style={{ maxWidth: '180px' }}
                >
                  {ORDER_STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <span className={`value status-${(order.status || '').toLowerCase().replace(/ /g, '-')}`}>
                  {order.status}
                </span>
              )}
            </div>
            <div className="info-row">
              <span className="label">Created By:</span>
              <span className="value">{order.created_by_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Created At:</span>
              <span className="value">{formatDateTimeDisplay(order.created_at)}</span>
            </div>
          </div>

          <div className="card">
            <h2>Customer Details</h2>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{order.customer_name}</span>
            </div>
            {order.customer_phone && (
              <div className="info-row">
                <span className="label">Phone:</span>
                <span className="value">{order.customer_phone}</span>
              </div>
            )}
            {order.customer_address && (
              <div className="info-row info-row-address">
                <span className="label">Address:</span>
                <span className="value address-value">
                  {order.customer_address.includes('\n')
                    ? order.customer_address.split('\n').filter(Boolean).map((line, i) => (
                        <span key={i} className="address-line">{line}</span>
                      ))
                    : order.customer_address}
                </span>
              </div>
            )}
          </div>

          <div className="card">
            <h2>Delivery Details</h2>
            <div className="info-row">
              <span className="label">Date:</span>
              <span className="value">{formatDateDisplay(order.delivery_date)}</span>
            </div>
            <div className="info-row">
              <span className="label">Time:</span>
              <span className="value">{order.delivery_time}</span>
            </div>
            {order.agent_name && (
              <div className="info-row">
                <span className="label">Agent:</span>
                <span className="value">{order.agent_name}</span>
              </div>
            )}
            {(showAmounts || (isStoreFrontOps && order.category === 'Online Orders')) && (
            <div className="info-row">
              <span className="label">Delivery Charge:</span>
              {isStoreFrontOps && order.category === 'Online Orders' ? (
                <select
                  className="form-control"
                  value={(() => {
                    const charge = Number(order.delivery_charge);
                    return charge === 100 || charge === 150 ? String(charge) : '';
                  })()}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) handleDeliveryChargeChange(v);
                  }}
                  disabled={updatingDeliveryCharge}
                  style={{ maxWidth: '120px' }}
                >
                  <option value="">— Select —</option>
                  <option value="100">₹100</option>
                  <option value="150">₹150</option>
                </select>
              ) : (
                <span className="value">
                  {order.delivery_charge != null && Number(order.delivery_charge) > 0
                    ? `₹${Number(order.delivery_charge)}`
                    : 'Not set'}
                </span>
              )}
            </div>
            )}
          </div>
        </div>

        {isEventCat && (
          <div className="card" style={{ marginTop: '1rem' }}>
            <h2>Event &amp; Caterer</h2>
            <div className="info-row">
              <span className="label">Party / Event:</span>
              <span className="value">{order.party_name || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Event date:</span>
              <span className="value">{formatDateDisplay(order.event_date || order.delivery_date)}</span>
            </div>
            <div className="info-row">
              <span className="label">Event time:</span>
              <span className="value">{order.event_time ? String(order.event_time).slice(0, 5) : '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Venue:</span>
              <span className="value">{order.event_venue || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Caterer:</span>
              <span className="value">{order.caterer_name || '—'}</span>
            </div>
            {order.caterer_phone && (
              <div className="info-row">
                <span className="label">Caterer phone:</span>
                <span className="value">{order.caterer_phone}</span>
              </div>
            )}
            {showAmounts && (
              <div className="info-row">
                <span className="label">Travel expense:</span>
                <span className="value">₹{parseFloat(order.travel_expense || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Payment & Settlement - only for Accountant and Admin */}
        {canManagePayments && (
        <div className="card payment-card">
          <h2>Payment & Settlement</h2>
          <div className="payment-summary">
            <div className="info-row">
              <span className="label">Total Amount</span>
              <span className="value">₹{parseFloat(order.total_amount || 0).toFixed(2)}</span>
            </div>
            <div className="info-row">
              <span className="label">Paid</span>
              <span className="value">₹{parseFloat(order.paid_amount || 0).toFixed(2)}</span>
            </div>
            <div className="info-row">
              <span className="label">Due</span>
              <span className={`value ${order.due_amount > 0 ? 'due-amount' : ''}`}>₹{parseFloat(order.due_amount || 0).toFixed(2)}</span>
            </div>
            <div className="info-row">
              <span className="label">Settlement</span>
              {isEventCat ? (
                <span className="value">Via Caterer SOA{order.caterer_name ? ` · ${order.caterer_name}` : ''}</span>
              ) : (
                <span className={`badge settlement-${(order.settlement_status || 'Pending').toLowerCase()}`}>
                  {isSettled ? (order.settlement_type || 'Settled') : 'Pending'}
                </span>
              )}
            </div>
            {!isEventCat && isSettled && order.settlement_notes && (
              <div className="info-row">
                <span className="label">Settlement Notes</span>
                <span className="value">{order.settlement_notes}</span>
              </div>
            )}
          </div>

          {order.payments && order.payments.length > 0 && (
            <div className="payment-history">
              <h3>Payment History</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Mode</th>
                    <th>Notes</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {order.payments.map(p => (
                    <tr key={p.id}>
                      <td>{formatDateDisplay(p.payment_date)}</td>
                      <td>₹{parseFloat(p.amount).toFixed(2)}</td>
                      <td>{p.payment_mode}</td>
                      <td>{p.notes || '-'}</td>
                      <td>{p.created_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canManagePayments && (!isSettled || isEventCat) && (
            <div className="payment-actions">
              {!showAddPayment ? (
                <>
                  {!isEventCat && (
                    <button type="button" className="btn btn-primary" onClick={() => {
                      setShowAddPayment(true);
                      setAddPaymentForm({ ...addPaymentForm, amount: String(order.due_amount || 0) });
                    }}>
                      Add Payment
                    </button>
                  )}
                  {isEventCat ? (
                    <div className="event-soa-hint" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                      <p style={{ margin: 0, color: '#555', fontSize: '0.95rem' }}>
                        Event / caterer dues are settled on the caterer statement of account
                        {order.caterer_id ? '' : ' (link a caterer on this order first)'}.
                      </p>
                      {order.caterer_id && (
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => navigate(`/caterers/${order.caterer_id}/account`)}
                        >
                          Open Caterer SOA
                        </button>
                      )}
                    </div>
                  ) : (
                    <button type="button" className="btn btn-success" onClick={() => setShowSettleModal(true)}>
                      Mark as Settled
                    </button>
                  )}
                </>
              ) : (
                <form onSubmit={handleAddPayment} className="add-payment-form">
                  <h4>Record Payment</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Amount</label>
                      <input type="number" step="0.01" min="0.01" className="form-control" value={addPaymentForm.amount} onChange={(e) => setAddPaymentForm({ ...addPaymentForm, amount: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Mode</label>
                      <select className="form-control" value={addPaymentForm.payment_mode} onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_mode: e.target.value })}>
                        {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input type="date" className="form-control" value={addPaymentForm.payment_date} onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_date: e.target.value })} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes (optional)</label>
                    <input type="text" className="form-control" value={addPaymentForm.notes} onChange={(e) => setAddPaymentForm({ ...addPaymentForm, notes: e.target.value })} />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddPayment(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={addPaymentSubmitting}>{addPaymentSubmitting ? 'Saving...' : 'Record Payment'}</button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
        )}

        <div className="card">
          <h2>Order Items</h2>
          {order.items && order.items.length > 0 ? (
            <div className="items-list">
              {order.items.map((item, index) => (
                <div key={item.id} className="item-card">
                  <div className="item-header">
                    <h3>{index + 1}. {getItemDisplayName(item)}</h3>
                    {showAmounts && <span className="item-price">₹{item.total_price}</span>}
                  </div>
                  
                  {item.item_type === 'Cakes' && (
                    <div className="item-details">
                      {item.flavour && <p><strong>Flavour:</strong> {item.flavour}</p>}
                      {(item.weight_kg != null || item.weight_decimal != null) && (Number(item.weight_kg || 0) + parseFloat(item.weight_decimal || 0) > 0) && (
                        <p><strong>Weight:</strong> {(Number(item.weight_kg || 0) + parseFloat(item.weight_decimal || 0)).toFixed(2).replace(/\.?0+$/, '')} KG</p>
                      )}
                      {showAmounts && item.cake_price && (
                        <p><strong>Price:</strong> ₹{item.cake_price}</p>
                      )}
                      {item.message_on_cake && (
                        <p><strong>Message on Cake:</strong> {item.message_on_cake}</p>
                      )}
                      {item.message_on_card && (
                        <p><strong>Message on Card:</strong> {item.message_on_card}</p>
                      )}
                      {item.cake_instructions && (
                        <p><strong>Special Instructions:</strong> {item.cake_instructions}</p>
                      )}
                      {item.files && item.files.length > 0 && (
                        <div className="cake-images">
                          <p><strong>Images:</strong></p>
                          <div className="image-grid">
                            {item.files.map((file, fileIndex) => (
                              <img
                                key={fileIndex}
                                src={getImageUrl(file.file_path)}
                                alt={`Cake ${fileIndex + 1}`}
                                style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px', borderRadius: '4px' }}
                                onError={(e) => {
                                  console.error('Image load error:', file.file_path);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {item.item_type === 'Bouquets' && (() => {
                    const bouquetFiles = (item.files || []).filter(f => f.file_type === 'bouquet_reference');
                    return bouquetFiles.length > 0 && (
                      <div className="item-details">
                        <div className="cake-images">
                          <p><strong>Bouquet Images:</strong></p>
                          <div className="image-grid">
                            {bouquetFiles.map((file, fileIndex) => (
                            <img
                              key={fileIndex}
                              src={getImageUrl(file.file_path)}
                              alt={`Bouquet ${fileIndex + 1}`}
                              style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px', borderRadius: '4px' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {item.item_type === 'Plants' && (() => {
                    const plantFiles = (item.files || []).filter(f => f.file_type === 'plant_reference');
                    return plantFiles.length > 0 && (
                      <div className="item-details">
                        <div className="cake-images">
                          <p><strong>Plant Reference Images:</strong></p>
                          <div className="image-grid">
                            {plantFiles.map((file, fileIndex) => (
                              <img
                                key={fileIndex}
                                src={getImageUrl(file.file_path)}
                                alt={`Plant ${fileIndex + 1}`}
                                style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px', borderRadius: '4px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {item.item_type === 'Catering' && (() => {
                    const cateringFiles = (item.files || []).filter(f => f.file_type === 'catering_pdf');
                    return cateringFiles.length > 0 && (
                      <div className="item-details">
                        <div className="cake-images">
                          <p><strong>Catering / Menu Files:</strong></p>
                          <div className="image-grid">
                            {cateringFiles.map((file, fileIndex) => {
                              const isImg = /\.(jpe?g|png|gif|webp)$/i.test(file.file_name || file.file_path || '');
                              return isImg ? (
                                <img
                                  key={fileIndex}
                                  src={getImageUrl(file.file_path)}
                                  alt={`Catering ${fileIndex + 1}`}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px', borderRadius: '4px' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <a key={fileIndex} href={getImageUrl(file.file_path)} target="_blank" rel="noopener noreferrer" style={{ display: 'block', margin: '5px', fontSize: '12px' }}>
                                  📄 {file.file_name || 'File'}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {item.item_type === 'Snack Boxes' && item.snack_box_items && (
                    <div className="item-details">
                      <strong>Items:</strong>
                      <ul>
                        {item.snack_box_items.map((snack, idx) => (
                          <li key={idx}>{snack.item_name} - Qty: {snack.quantity}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.special_instructions && (
                    <p><strong>Instructions:</strong> {item.special_instructions}</p>
                  )}
                  
                  <p><strong>Quantity:</strong> {item.quantity}</p>
                </div>
              ))}
            </div>
          ) : (
            <p>No items found</p>
          )}
          
          {showAmounts && (
          <div className="order-total">
            <strong>Total Amount: ₹{order.total_amount}</strong>
            {(order.paid_amount > 0 || order.due_amount > 0) && (
              <div className="order-total-breakdown">
                <span>Paid: ₹{parseFloat(order.paid_amount || 0).toFixed(2)}</span>
                <span>Due: ₹{parseFloat(order.due_amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Delivery Charge Modal - Store Manager must select before printing delivery challan */}
        {showDeliveryChargeModal && (
          <div className="modal-overlay" onClick={() => !printingDeliveryChallan && setShowDeliveryChargeModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Select Delivery Charge</h3>
              <p>Please select delivery charge for this Online Order before printing the delivery challan.</p>
              <div className="form-group">
                <label>Delivery Charge</label>
                <select
                  className="form-control"
                  value={deliveryChargeForPrint}
                  onChange={(e) => setDeliveryChargeForPrint(e.target.value)}
                  disabled={printingDeliveryChallan}
                  style={{ maxWidth: '150px' }}
                >
                  <option value="">— Select —</option>
                  <option value="100">₹100</option>
                  <option value="150">₹150</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeliveryChargeModal(false)} disabled={printingDeliveryChallan}>Cancel</button>
                <button type="button" className="btn btn-success" onClick={handleConfirmDeliveryChargeAndPrint} disabled={!deliveryChargeForPrint || printingDeliveryChallan}>
                  {printingDeliveryChallan ? 'Saving & Printing...' : 'Confirm & Print'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settle Modal */}
        {showSettleModal && (
          <div className="modal-overlay" onClick={() => setShowSettleModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Settle Order - {order.order_number}</h3>
              <p className="modal-due">Total: ₹{parseFloat(order.total_amount || 0).toFixed(2)} | Due: ₹{parseFloat(order.due_amount || 0).toFixed(2)}</p>
              <form onSubmit={handleSettle}>
                <div className="form-group">
                  <label>Settlement Type</label>
                  <select className="form-control" value={settleForm.settlement_type} onChange={(e) => setSettleForm({ ...settleForm, settlement_type: e.target.value })}>
                    {SETTLEMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {(settleForm.settlement_type === 'Write-off' || settleForm.settlement_type === 'Credit') && (
                  <div className="form-group">
                    <label>Notes (required)</label>
                    <textarea className="form-control" value={settleForm.settlement_notes} onChange={(e) => setSettleForm({ ...settleForm, settlement_notes: e.target.value })} placeholder="e.g. Internal employee - called to credit" rows={3} required />
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowSettleModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success" disabled={settleSubmitting}>{settleSubmitting ? 'Settling...' : 'Mark as Settled'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;

